<template>
  <StatCard
    title="Created vs completed"
    :subtitle="subtitle"
    :loading="loading"
    :empty="isEmpty"
    empty-title="No activity in this period"
    empty-hint="Tasks you create and finish will chart here."
  >
    <template #legend>
      <span class="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
        <span class="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block"></span> Created
      </span>
      <span class="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 ml-3">
        <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span> Completed
      </span>
    </template>
    <div ref="chartEl" class="w-full h-64"></div>
  </StatCard>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import StatCard from './StatCard.vue'
import { useChartLabels } from './useChartLabels.js'

const props = defineProps({
  data: { type: Array, default: () => [] },
  bucket: { type: String, default: 'day' },
  loading: { type: Boolean, default: false },
})

const { bucketLabel } = useChartLabels()
const chartEl = ref(null)
let chart = null

const isEmpty = computed(() =>
  !props.loading && props.data.every(d => d.created === 0 && d.completed === 0))

const subtitle = computed(() => {
  const per = { day: 'day', week: 'week', month: 'month' }[props.bucket] || 'day'
  return `Per ${per}. Completed trailing created for a stretch is a backlog forming.`
})

function render() {
  if (!chartEl.value || !props.data.length) return
  // Highcharts arrives from a CDN via layouts/_highcharts.html.erb. If that is
  // blocked or still in flight, degrade to an empty chart area rather than
  // throwing and taking the rest of the page down with it.
  if (typeof Highcharts === 'undefined') return
  if (chart) { chart.destroy(); chart = null }

  const labels = props.data.map(d => bucketLabel(d.date, props.bucket))
  chart = Highcharts.chart(chartEl.value, {
    chart: { type: 'areaspline', height: 256, spacing: [16, 16, 12, 8] },
    xAxis: {
      categories: labels,
      tickInterval: Math.max(1, Math.ceil(labels.length / 7)),
      crosshair: { width: 1 },
    },
    yAxis: { min: 0, allowDecimals: false, title: { text: null } },
    tooltip: { shared: true },
    legend: { enabled: false },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.08,
        lineWidth: 2,
        marker: { enabled: false, symbol: 'circle', radius: 3, states: { hover: { enabled: true } } },
      },
    },
    series: [
      { name: 'Created', data: props.data.map(d => d.created), color: '#2563eb' },
      { name: 'Completed', data: props.data.map(d => d.completed), color: '#10b981' },
    ],
  })
}

watch(() => props.data, () => nextTick(render), { deep: true })
onMounted(() => { if (!isEmpty.value) nextTick(render) })
// Without this every visit to the tab leaks a chart instance.
onUnmounted(() => { if (chart) { chart.destroy(); chart = null } })
</script>
